/**
 * Iter 313 VA-1b — Owner View Through banner + details drawer.
 *
 * Sticky banner shown whenever View Through is active. Copy is required
 * verbatim: "Viewing through: {Context}. Actions are recorded as {Owner}."
 * Never uses "Preview mode", "Impersonating", or "Actions disabled".
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useViewThrough, isPathSupportedByViewThrough, VIEW_THROUGH_SUPPORTED_PATHS } from "@/lib/viewThroughContext";
import { API, getAuthHeaders } from "@/lib/portalUtils";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { AlertCircle, CheckCircle2 } from "lucide-react";

const _hasFlagSample = ["staff_assist_assign_enabled", "view_through_enabled",
  "redundant_receptionist_enabled", "redundant_assignment_writes_enabled"];

export const ViewThroughBanner = ({ ownerLabel }) => {
  const { viewThrough, isActive, exit } = useViewThrough();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  // Simplification Pass (2026-07-09) — banner truth.
  // The banner MUST tell the operator whether the current surface really
  // honors the viewed permission context, so they never assume parity
  // that doesn't exist.
  const pathSupported = isPathSupportedByViewThrough(location.pathname);

  useEffect(() => {
    if (!detailsOpen || !isActive) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [cur, flags] = await Promise.all([
          axios.get(`${API}/view-through/current`, getAuthHeaders()),
          // Perm-Drift-Fix (2026-07-09): the correct endpoint is
          // `/feature-flags` (no `/platform` suffix). The old URL returned
          // 404 and the `.catch` silently swallowed it, so the drawer
          // displayed every flag as `—` even when it was actually ON —
          // which mislead the operator into thinking the app was
          // dark-launched off. Log any real failure now.
          axios.get(`${API}/feature-flags`, getAuthHeaders()).catch((e) => {
            console.warn("view_through drawer: failed to load feature flags", e?.response?.status);
            return { data: {} };
          }),
        ]);
        if (cancelled) return;
        setDetails({
          view_through: cur?.data?.view_through || null,
          flags: flags?.data || {},
        });
      } catch (_e) {
        if (!cancelled) setDetails({ view_through: null, flags: {} });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [detailsOpen, isActive, viewThrough]);

  if (!isActive) return null;
  return (
    <>
      <div
        className={`sticky top-0 z-50 w-full border-b backdrop-blur px-4 py-2 text-sm flex items-center gap-3 shadow-sm ${
          pathSupported
            ? "border-amber-400/60 bg-amber-50/95 text-amber-900"
            : "border-red-400/60 bg-red-50/95 text-red-900"
        }`}
        data-testid="view-through-banner"
        data-vt-path-supported={pathSupported ? "true" : "false"}
      >
        {pathSupported ? (
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" aria-hidden />
        ) : (
          <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden />
        )}
        <span className="font-medium" data-testid="view-through-banner-text">
          Viewing through: <span className="font-semibold">{viewThrough.target_label}</span>.
          Actions are recorded as <span className="font-semibold">{ownerLabel || "the logged-in owner"}</span>.
          {" "}
          <span className="opacity-90" data-testid="view-through-scope-note">
            {pathSupported
              ? "Supported here: full workflow — actions gate on the viewed context, audit stays with the owner."
              : "This surface is NOT supported — actions are disabled."}
          </span>
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost" size="sm"
            className={pathSupported ? "text-amber-900 hover:bg-amber-100" : "text-red-900 hover:bg-red-100"}
            onClick={() => setDetailsOpen(true)}
            data-testid="view-through-details-btn"
          >
            Show details
          </Button>
          <Button
            variant="outline" size="sm"
            className={pathSupported ? "border-amber-500 text-amber-900 hover:bg-amber-100" : "border-red-500 text-red-900 hover:bg-red-100"}
            onClick={exit}
            data-testid="view-through-exit-btn"
          >
            Exit
          </Button>
        </div>
      </div>
      <Sheet open={detailsOpen} onOpenChange={setDetailsOpen}>
        <SheetContent side="right" className="w-[420px] sm:w-[520px] overflow-y-auto"
                      data-testid="view-through-details-drawer">
          <SheetHeader>
            <SheetTitle>View Through details</SheetTitle>
            <SheetDescription>
              You are viewing the app through a selected permission context. Your account remains accountable for every action.
            </SheetDescription>
          </SheetHeader>
          {loading ? (
            <div className="mt-6 text-sm text-slate-500">Loading…</div>
          ) : details?.view_through ? (
            <div className="mt-6 space-y-5 text-sm">
              <section data-testid="view-through-details-actor">
                <h3 className="font-semibold mb-1">Real actor (accountable)</h3>
                <div className="rounded border border-slate-200 bg-slate-50 p-2">
                  <div><span className="text-slate-500">Email:</span> {details.view_through.real_actor?.email}</div>
                  <div><span className="text-slate-500">Role:</span> {details.view_through.real_actor?.role}</div>
                </div>
              </section>
              <section data-testid="view-through-details-context">
                <h3 className="font-semibold mb-1">Viewed context</h3>
                <div className="rounded border border-amber-200 bg-amber-50 p-2">
                  <div className="flex items-center gap-2">
                    {/* Perm-Drift-Fix (2026-07-09) — explicit USER vs ROLE
                        badge. Previously the label alone was ambiguous
                        (e.g. a user literally named "Receptionist" looks
                        identical to a role target labelled "Receptionist"),
                        which caused the owner to conflate the two during
                        a P0 verification. The badge is required. */}
                    {details.view_through.target_type === "user" ? (
                      <span className="inline-flex items-center rounded bg-blue-600 text-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                            data-testid="view-through-target-badge-user">USER</span>
                    ) : (
                      <span className="inline-flex items-center rounded bg-purple-600 text-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                            data-testid="view-through-target-badge-role">ROLE</span>
                    )}
                    <div className="font-medium">{details.view_through.target_label}</div>
                  </div>
                  {details.view_through.target_type === "user" && details.view_through.target_key ? (
                    <div className="text-xs text-amber-800/80 mt-1">
                      User id: <span className="font-mono">{details.view_through.target_key}</span>
                    </div>
                  ) : null}
                  <div className="text-xs text-amber-800/80 mt-1">Permission hash: {details.view_through.permission_hash}</div>
                </div>
              </section>
              <section data-testid="view-through-details-permissions">
                <h3 className="font-semibold mb-1">Effective permissions</h3>
                <div className="rounded border border-slate-200 max-h-60 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">Section</th>
                        <th className="text-left px-2 py-1">Read</th>
                        <th className="text-left px-2 py-1">Write</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(details.view_through.effective_permissions || {}).map(([sec, p]) => (
                        <tr key={sec} className="border-t border-slate-100">
                          <td className="px-2 py-1 font-mono">{sec}</td>
                          <td className="px-2 py-1">{p?.read ? "✓" : "—"}</td>
                          <td className="px-2 py-1">{p?.write ? "✓" : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
              <section data-testid="view-through-details-scope">
                <h3 className="font-semibold mb-1">Supported scope</h3>
                <div className="rounded border border-slate-200 p-2 text-xs space-y-1">
                  <p className="text-slate-700">
                    View Through is only fully honored on the routes below.
                    Everywhere else, actions are disabled by a fail-closed
                    shield to prevent silent execution against owner
                    permissions.
                  </p>
                  <ul className="list-disc pl-5 mt-1">
                    {VIEW_THROUGH_SUPPORTED_PATHS.map((p) => (
                      <li key={p} className="font-mono">{p}</li>
                    ))}
                  </ul>
                </div>
              </section>
              <section data-testid="view-through-details-flags">
                <h3 className="font-semibold mb-1">Relevant feature flags</h3>
                <div className="rounded border border-slate-200 p-2 text-xs">
                  {_hasFlagSample.map((k) => (
                    <div key={k} className="flex justify-between">
                      <span className="font-mono">{k}</span>
                      <span>{details.flags?.[k] === true ? "ON" : details.flags?.[k] === false ? "off" : "—"}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          ) : (
            <div className="mt-6 text-sm text-slate-500">No context.</div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default ViewThroughBanner;
